import AddIcon from "@mui/icons-material/AddRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { useMemo } from "react";
import type { ApprovalMethod } from "@entrilia/shared/api/workflowTypes";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { Field, ObjectFilter } from "../../../../api/types/objectTypes";
import type { ApprovalRule } from "../../../../api/types/workflowTypes";
import MultiselectAutocomplete from "../../../common/MultiselectAutocomplete";
import ObjectFilterEditor from "../../../object-filters/ObjectFilterEditor";
import type { ObjectFilterFieldGroup } from "../../../object-filters/ObjectFilterFieldSelect";
import ApprovalConfigurationContext from "./ApprovalConfigurationContext";
import useClientContext from "../../../../context/useClientContext";
import { getApprovalRuleObjectFilter, getApprovalRuleSelectOptions } from "./approvalConfigurationHelper";
import {
  removeFilterAction,
  removeRuleAction,
  setApprovalMethodAction,
  setApprovalRuleGroupIdsAction,
  setFieldAction,
  setFilterAction,
} from "./approvalConfigurationState";

interface Props {
  rule: ApprovalRule;
  ruleIndex: number;
}

const createApprovalMethod = (value: string): ApprovalMethod => {
  return { requireAll: value === "Everyone", minimumApprovals: 1 };
};

const EditApprovalRuleCard = ({ rule, ruleIndex }: Props) => {
  const { dictionaries } = useClientContext();
  const {
    setState,
    clientUserGroups,
    editedStepValidationResults,
    approvalConfiguration,
    lookupOptionsByObjectType,
    lookupOptionsUnavailable,
  } = useDefinedContext(ApprovalConfigurationContext);

  const availableFieldGroups: ObjectFilterFieldGroup[] = useMemo(
    () =>
      approvalConfiguration.approvalRuleFieldSources.map((source) => ({
        key: source.relatedObjectType ?? source.scope,
        label: source.label,
        fields: source.fields,
      })),
    [approvalConfiguration.approvalRuleFieldSources]
  );

  const ruleGroupKey = rule.relatedObjectType ?? rule.fieldScope;
  const firstSource = approvalConfiguration.approvalRuleFieldSources.find((source) => source.fields.length > 0);
  const firstField = firstSource?.fields[0];
  const ruleFilter = getApprovalRuleObjectFilter(rule);
  const selectedSource = approvalConfiguration.approvalRuleFieldSources.find(
    (source) => (source.relatedObjectType ?? source.scope) === ruleGroupKey
  );
  const selectedField = selectedSource?.fields.find((field) => field.id === rule.fieldId);
  const selectOptions = getApprovalRuleSelectOptions(
    selectedField,
    lookupOptionsByObjectType[rule.relatedObjectType ?? approvalConfiguration.objectType ?? ""],
    dictionaries
  );

  const selectedGroups = clientUserGroups.filter((group) => rule.groupIds.includes(group.id));
  const lookupOptionsError =
    selectedField?.configuration?.$type === "Lookup" && lookupOptionsUnavailable
      ? "Lookup options are unavailable. Stored selections are preserved."
      : undefined;
  const validationError = editedStepValidationResults?.rules[ruleIndex]?.filter.error;
  const filterError =
    selectOptions?.length === 0 ? (lookupOptionsError ?? validationError) : (validationError ?? lookupOptionsError);

  const approvalMethodValue = rule.method.requireAll ? "Everyone" : "Anyone"; // We don't support minimumApprovals number yet

  const handleFieldChange = (field: Field, groupKey: string) => {
    const fieldSource = approvalConfiguration.approvalRuleFieldSources.find(
      (source) => (source.relatedObjectType ?? source.scope) === groupKey
    );
    if (fieldSource) {
      setState(setFieldAction(ruleIndex, fieldSource, field));
    }
  };

  const handleFilterChange = (filter: ObjectFilter) => {
    setState(setFilterAction(ruleIndex, filter));
  };

  const handleAddConditionClick = () => {
    if (!firstSource || !firstField) {
      return;
    }

    setState(setFieldAction(ruleIndex, firstSource, firstField));
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2} direction="row" sx={{ alignItems: "center" }}>
          <MultiselectAutocomplete
            options={clientUserGroups}
            values={selectedGroups}
            onSelected={(selected) =>
              setState(
                setApprovalRuleGroupIdsAction(
                  ruleIndex,
                  selected.map((group) => group.id)
                )
              )
            }
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            label="User Groups"
          />

          <FormControl sx={{ width: "10rem" }}>
            <InputLabel id="approval-method-select-label">Approval Method</InputLabel>
            <Select
              labelId="approval-method-select-label"
              label="Approval Method"
              value={approvalMethodValue}
              onChange={(e) => setState(setApprovalMethodAction(ruleIndex, createApprovalMethod(e.target.value)))}
            >
              <MenuItem value="Anyone">Anyone</MenuItem>
              <MenuItem value="Everyone">Everyone</MenuItem>
            </Select>
          </FormControl>

          <IconButton color="error" onClick={() => setState(removeRuleAction(ruleIndex))}>
            <DeleteIcon />
          </IconButton>
        </Stack>

        {editedStepValidationResults?.rules[ruleIndex]?.groupIds.error && (
          <FormHelperText>{editedStepValidationResults?.rules[ruleIndex]?.groupIds.error}</FormHelperText>
        )}

        {availableFieldGroups.length > 0 && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Divider flexItem sx={{ borderStyle: "dashed" }} />

            {ruleFilter && (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <ObjectFilterEditor
                  startLabel="Where"
                  filter={ruleFilter}
                  fieldGroupKey={ruleGroupKey}
                  availableFieldGroups={availableFieldGroups}
                  onFieldChange={handleFieldChange}
                  onFilterChange={handleFilterChange}
                  selectOptions={selectOptions}
                  error={filterError}
                />
                <CloseIconButton onClick={() => setState(removeFilterAction(ruleIndex))} />
              </Stack>
            )}

            {!ruleFilter && (
              <Button
                variant="text"
                color="primary"
                startIcon={<AddIcon />}
                sx={{ width: "fit-content" }}
                onClick={handleAddConditionClick}
              >
                Add Condition
              </Button>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default EditApprovalRuleCard;
